import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  MultiSelectOption,
  MultiSelectProps,
  MultiSelectGroup,
  GroupInnerLogic,
  GroupsRelation,
} from '@app/shared/components/MultiSelect/types';

import type { SelectProps } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';

// Отложенная фильтрация опций: столько же, сколько в MultipleSelect.
const SEARCH_DEBOUNCE_MS = 300;

const uid = (): string =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

const makeEmptyGroup = (withRelation: boolean): MultiSelectGroup => ({
  id: uid(),
  logic: 'AND',
  relationToPrev: withRelation ? 'AND' : undefined,
  selectedValues: [],
});

type UseMultiSelectArgs = Pick<
  MultiSelectProps,
  'options' | 'value' | 'onChange'
>;

export const useMultiSelect = ({
  options,
  value,
  onChange,
}: UseMultiSelectArgs) => {
  // `searchByGroup` — то, что видно в поле поиска группы: обновляется на
  // каждый символ. `appliedSearchByGroup` — запрос, по которому фильтруются
  // опции: он отстаёт на debounce, поэтому длинный список не пересобирается
  // на каждое нажатие и ввод не подвисает (issue/11161; тот же приём, что в
  // MultipleSelect после issue/11072).
  const [searchByGroup, setSearchByGroup] = useState<Record<string, string>>(
    {}
  );
  const [appliedSearchByGroup, setAppliedSearchByGroup] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!value?.groups?.length) {
      onChange({ groups: [makeEmptyGroup(false)] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.groups?.length]);

  const optionByValue = useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    for (const o of options) map.set(o.value, o);
    return map;
  }, [options]);

  const groups: MultiSelectGroup[] = useMemo(
    () => (value?.groups?.length ? value.groups : [makeEmptyGroup(false)]),
    [value?.groups]
  );

  const allSelectedValues: string[] = useMemo(() => {
    const s = new Set<string>();
    for (const g of groups) for (const v of g.selectedValues) s.add(v);
    return [...s];
  }, [groups]);

  const getDisplayLabel = useCallback(
    (v: string): string => {
      const opt = optionByValue.get(v);
      if (!opt) return v;
      return opt.isDeleted ? `УДАЛЕН ${opt.label}` : opt.label;
    },
    [optionByValue]
  );

  const tagRender: SelectProps<string[]>['tagRender'] = useCallback(
    (props: CustomTagProps) => {
      const { value: v, closable, onClose } = props;
      const label = getDisplayLabel(String(v));
      return (
        <span className="multi-select__tag">
          <span className="multi-select__tag__text">{label}</span>
          {closable && (
            <span
              role="button"
              aria-label="remove"
              className="multi-select__tag__close"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClose}
            >
              ×
            </span>
          )}
        </span>
      );
    },
    [getDisplayLabel]
  );

  const getFilteredOptions = useCallback(
    (groupId: string): MultiSelectOption[] => {
      const q = (appliedSearchByGroup[groupId] ?? '').trim().toLowerCase();
      if (!q) return options;
      return options.filter((o) => o.label.toLowerCase().includes(q));
    },
    [options, appliedSearchByGroup]
  );

  const applySearch = useMemo(
    () =>
      debounce((groupId: string, val: string) => {
        setAppliedSearchByGroup((prev) => ({ ...prev, [groupId]: val }));
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const setGroupSearch = useCallback(
    (groupId: string, val: string) => {
      setSearchByGroup((prev) => ({ ...prev, [groupId]: val }));
      applySearch(groupId, val);
    },
    [applySearch]
  );

  const resetAllSearches = useCallback(() => {
    applySearch.cancel();
    setSearchByGroup({});
    setAppliedSearchByGroup({});
  }, [applySearch]);

  const commitGroups = useCallback(
    (next: MultiSelectGroup[]) => {
      onChange({ groups: next });
    },
    [onChange]
  );

  const addGroup = useCallback(() => {
    const hasAny = groups.length > 0;
    const next = [...groups, makeEmptyGroup(hasAny)];
    commitGroups(next);
  }, [groups, commitGroups]);

  const removeGroup = useCallback(
    (groupId: string) => {
      if (groups.length <= 1) return;
      const idx = groups.findIndex((g) => g.id === groupId);
      if (idx < 0) return;

      const next = groups
        .filter((g) => g.id !== groupId)
        .map((g) => ({ ...g }));
      if (idx === 0 && next[0]) {
        next[0].relationToPrev = undefined;
      }
      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const setGroupLogic = useCallback(
    (groupId: string, logic: GroupInnerLogic) => {
      const next = groups.map((group) => {
        if (group.id !== groupId) return group;

        return { ...group, logic };
      });

      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const setBetweenRelation = useCallback(
    (groupId: string, relation: GroupsRelation) => {
      const idx = groups.findIndex((group) => group.id === groupId);
      if (idx < 0) return;

      const next = groups.map((group) => {
        if (group.id !== groupId) return group;

        return { ...group, relationToPrev: relation };
      });

      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const toggleValueInGroup = useCallback(
    (groupId: string, valueToToggle: string, checked: boolean) => {
      const next = groups.map((group) => {
        if (group.id !== groupId) return group;

        const set = new Set(group.selectedValues);

        if (checked) {
          set.add(valueToToggle);
        } else {
          set.delete(valueToToggle);
        }

        return { ...group, selectedValues: [...set] };
      });
      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const selectAllFilteredInGroup = useCallback(
    (groupId: string) => {
      const filtered = getFilteredOptions(groupId).map(({ value }) => value);
      const filteredSet = new Set(filtered);

      const next = groups.map((group) => {
        if (group.id !== groupId) return group;

        const set = new Set(group.selectedValues);

        filteredSet.forEach((value) => set.add(value));

        return { ...group, selectedValues: [...set] };
      });
      commitGroups(next);
    },
    [groups, getFilteredOptions, commitGroups]
  );

  const resetGroupSelection = useCallback(
    (groupId: string) => {
      const next = groups.map((group) => {
        if (group.id !== groupId) return group;

        return { ...group, selectedValues: [] };
      });

      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const handleTagDeselect = useCallback(
    (valueToRemove: string) => {
      if (!valueToRemove) return;

      const next = groups.map((group) => ({
        ...group,
        selectedValues: group.selectedValues.filter(
          (value) => value !== valueToRemove
        ),
      }));

      commitGroups(next);
    },
    [groups, commitGroups]
  );

  const clearAll = useCallback(() => {
    resetAllSearches();
    onChange({ groups: [makeEmptyGroup(false)] });
  }, [onChange, resetAllSearches]);

  const renderOptionLabel = useCallback(
    ({ label }: MultiSelectOption) => label,
    []
  );

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (open) return;

      resetAllSearches();
    },
    [resetAllSearches]
  );

  return {
    groups,
    allSelectedValues,
    optionByValue,
    searchByGroup,
    setGroupSearch,
    getFilteredOptions,
    renderOptionLabel,
    setGroupLogic,
    setBetweenRelation,
    toggleValueInGroup,
    selectAllFilteredInGroup,
    resetGroupSelection,
    addGroup,
    removeGroup,
    handleTagDeselect,
    clearAll,
    tagRender,
    handleDropdownVisibleChange,
    getDisplayLabel,
    resetAllSearches,
  };
};
